import {useEffect, useMemo, useCallback, useRef} from '@wordpress/element';
import $ from 'jquery';
import {__} from '@wordpress/i18n';
import {isEmpty} from 'lodash';
import {
    extractSelectedShippingOption,
    removeNumberPrecision,
    getCartTotalItem,
    extractFullName,
    getShippingOptionId,
    cartContainsSubscription,
    DEFAULT_BILLING_ADDRESS,
    DEFAULT_SHIPPING_ADDRESS
} from "../../utils";
import {getSelectedShippingOptionId} from "../../googlepay/helpers";
import {usePaymentMethodDataContext} from "../../context";

export const usePayPalOptions = (
    {
        getData,
        addNotice,
        paypal,
        paypalCheckoutInstance,
        billing,
        shippingData,
        eventRegistration,
        emitResponse,
        onClick,
        onClose
    }) => {
    const {cartTotal, currency} = billing;
    const {needsShipping} = shippingData;
    const {noticeContexts, responseTypes} = emitResponse;
    const currentData = useRef({billing, shippingData});
    const paymentData = useRef({});
    const {
        onSubmit,
        onPaymentDataFilter,
        setPaymentMethodData,
    } = usePaymentMethodDataContext();
    const {onCheckoutFail} = eventRegistration;

    useEffect(() => {
        const unsubscribe = onCheckoutFail(() => {
            if (paymentData.current.incompleteBilling) {
                return {
                    type: responseTypes.ERROR,
                    message: __('Please complete all required billing fields below then click Place Order.', 'woo-payment-gateway'),
                    messageContext: noticeContexts.EXPRESS_PAYMENTS
                };
            }
            return null;
        });
        return () => unsubscribe();
    }, [
        onCheckoutFail,
        responseTypes,
        noticeContexts
    ]);

    const getButtonStyle = useCallback((type) => {
        const baseStyle = getData('buttonStyle');
        switch (type) {
            case paypal.FUNDING.PAYPAL:
                return baseStyle;
            case paypal.FUNDING.PAYLATER:
                return {...baseStyle, ...getData('bnplButtonStyle')};
            default:
                return baseStyle;

        }
    }, [paypal]);

    const getFormattedShippingOptions = (shippingRates) => {
        let options = [];
        shippingRates.forEach((shippingPackage, idx) => {
            let rates = shippingPackage.shipping_rates.map(rate => {
                let txt = document.createElement('textarea');
                txt.innerHTML = rate.name;
                return {
                    id: getShippingOptionId(idx, rate.rate_id),
                    label: txt.value,
                    type: 'SHIPPING',
                    selected: rate.selected,
                    amount: {
                        value: removeNumberPrecision(rate.price, rate.currency_minor_unit),
                        currency_code: rate.currency_code
                    }
                }
            });
            options = [...options, ...rates];
        });
        return options;
    };

    useEffect(() => {
        currentData.current = {billing, shippingData, onClick, onClose, onSubmit, addNotice};
    }, [
        billing,
        shippingData,
        onClick,
        onClose,
        onSubmit,
        addNotice
    ]);

    useEffect(() => {
        const unsubscribe = onPaymentDataFilter((data, {nonceKey, billing, shippingData}) => {
            data.meta.paymentMethodData[nonceKey] = paymentData.current.nonce;
            data.meta.billingAddress = {
                ...DEFAULT_BILLING_ADDRESS,
                ...billing.billingAddress,
                ...paymentData.current.billingAddress
            };
            data.meta.shippingAddress = {
                ...DEFAULT_SHIPPING_ADDRESS,
                ...shippingData.shippingAddress,
                ...paymentData.current.shippingAddress
            }

            return data;
        }, 5);
        return () => unsubscribe();
    }, [onPaymentDataFilter]);

    const onShippingChange = useCallback((address, selectedShippingOption) => {
        return new Promise((resolve, reject) => {
            $.ajax({
                method: 'POST',
                url: getData('routes').shipping,
                data: {
                    payment_method: 'braintree_paypal',
                    address: address,
                    shipping_method: selectedShippingOption
                },
                dataType: 'json',
                success: function (result, status, jqXHR) {
                    if (result.code) {
                        reject(result);
                    } else {
                        resolve(result);
                    }
                }.bind(this),
                error: function (xhr) {
                    reject(xhr);
                }.bind(this)
            })
        });
    }, []);

    const options = useMemo(() => {
        if (paypal && paypalCheckoutInstance) {
            const options = [];
            const sources = [paypal.FUNDING.PAYPAL];
            if (getData('bnplEnabled')) {
                sources.push(paypal.FUNDING.PAYLATER);
            }
            for (let fundingSource of sources) {
                const option = {
                    fundingSource,
                    style: getButtonStyle(fundingSource),
                    onError: (error) => {
                        currentData.current.onClose();
                        currentData.current.addNotice(error);
                    },
                    onClick: () => {
                        currentData.current.onClick();
                    },
                    onApprove: (data, actions) => {
                        return paypalCheckoutInstance.tokenizePayment(data).then(response => {
                            const {billingAddress} = currentData.current.billing;
                            const {needsShipping} = currentData.current.shippingData;
                            // set shipping address
                            paymentData.current = {nonce: response.nonce, billingAddress: {}};
                            if (response?.details?.billingAddress?.line1) {
                                const address = response.details.billingAddress;
                                paymentData.current.billingAddress = {
                                    address_1: address.line1,
                                    address_2: address.line2,
                                    city: address.city,
                                    state: address.state,
                                    postcode: address.postalCode,
                                    country: address.countryCode
                                }
                            }
                            if (!isEmpty(response?.details?.shippingAddress)) {
                                const {shippingAddress} = response.details;
                                const [first_name, last_name] = extractFullName(shippingAddress.recipientName);
                                paymentData.current.shippingAddress = {
                                    first_name,
                                    last_name,
                                    address_1: shippingAddress.line1 || '',
                                    address_2: shippingAddress.line2 || '',
                                    city: shippingAddress.city || '',
                                    state: shippingAddress.state || '',
                                    postcode: shippingAddress.postalCode || '',
                                    country: shippingAddress.countryCode || ''
                                }
                                if (isEmpty(paymentData.current.billingAddress)) {
                                    paymentData.current.billingAddress = paymentData.current.shippingAddress;
                                }
                                if (response.details?.phone) {
                                    paymentData.current.shippingAddress.phone = response.details.phone;
                                }
                            }
                            if (response.details?.phone) {
                                paymentData.current.billingAddress.phone = response.details.phone;
                            }
                            if (response.details?.email) {
                                paymentData.current.billingAddress.email = response.details.email;
                            }
                            if (response.details?.firstName) {
                                paymentData.current.billingAddress.first_name = response.details.firstName;
                            }
                            if (response.details?.lastName) {
                                paymentData.current.billingAddress.last_name = response.details.lastName;
                            }
                            if (!paymentData.current.billingAddress.address_1 && !billingAddress.address_1) {
                                paymentData.current.incompleteBilling = true;
                            }
                            if (cartContainsSubscription() && needsShipping) {
                                currentData.current.addNotice(__('Please select a shipping option then click Place Order to complete your payment.', 'woo-payment-gateway'), 'info');
                            } else {
                                paymentData.current.nonce = response.nonce;
                                setPaymentMethodData({
                                    nonce: response.nonce
                                });
                            }
                        });
                    },
                    createOrder: () => {
                        return paypalCheckoutInstance.createPayment({
                            flow: cartContainsSubscription() ? 'vault' : 'checkout',
                            intent: getData('intent'),
                            currency: currency.code,
                            displayName: getData('displayName'),
                            amount: removeNumberPrecision(cartTotal.value, currency.minorUnit),
                            enableShippingAddress: needsShipping,
                            shippingAddressEditable: needsShipping
                        }).then(id => {
                            return id;
                        }).catch(error => {
                            console.log(error);
                        })
                    },
                    onCancel: () => {
                        currentData.current.onClose();
                    }
                }
                if (cartContainsSubscription()) {
                    option.createBillingAgreement = option.createOrder;
                    delete (option.createOrder);
                }
                // can't update shipping options when using billing agreements.
                if (needsShipping && !option.hasOwnProperty('createBillingAgreement')) {
                    option.onShippingChange = (data, actions) => {
                        const {shipping_address: address, selected_shipping_option = null} = data;
                        const selectedShippingOption = selected_shipping_option?.id || null;
                        const shippingData = currentData.current.shippingData;
                        const newAddress = {
                            city: address.city || '',
                            state: address.state || '',
                            postcode: address.postal_code || '',
                            country: address.country_code || ''
                        }
                        return onShippingChange(newAddress, selectedShippingOption).then(response => {
                            const patch = response.data.braintree_paypal.patch;
                            return actions.order.patch(patch).then(() => {
                                return actions.resolve();
                            });
                        }).finally(() => {
                            if (selectedShippingOption) {
                                shippingData.setSelectedRates(...extractSelectedShippingOption(selectedShippingOption));
                            }
                        });
                    }
                }
                let button = paypal.Buttons(option);
                if (button.isEligible()) {
                    options.push(option);
                }
            }
            return options;
        }
    }, [
        paypal,
        needsShipping,
        onShippingChange,
        setPaymentMethodData,
        paypalCheckoutInstance
    ]);
    return options;
}

export default usePayPalOptions;